import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Modal, Input, Button, Spin, message } from "antd";
import { IBusinessFull } from "../../../../interfaces/businessFull.interface";
import styles from "./AdyenSubmitModal.module.css";
import { adyenOnboardingServer } from "../../businessesApi";

export interface AdyenErrorResponse {
  message?: string;
  statusCode?: number;
  code?: string;
  type?: string;
  invalidFields?: Array<{ name?: string; value?: unknown; message?: string }>;
}

interface AdyenSubmitModalProps {
  visible: boolean;
  business: IBusinessFull | null;
  onClose: () => void;
  onSubmitted?: (businessId: string, onboardingLink: string) => void;
  // Invoked when the submit was rejected because the merchant has no website on
  // record. The parent should open the Company Profile editor focused on the
  // Website field so the user can add it before retrying.
  onAddWebsite?: (business: IBusinessFull) => void;
}

type Step = "confirm" | "loading" | "success" | "error";

export default function AdyenSubmitModal({
  visible,
  business,
  onClose,
  onSubmitted,
  onAddWebsite,
}: AdyenSubmitModalProps) {
  const [step, setStep] = useState<Step>("confirm");
  const [onboardingUrl, setOnboardingUrl] = useState("");
  const [adyenError, setAdyenError] = useState<AdyenErrorResponse | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleClose = () => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setStep("confirm");
    setOnboardingUrl("");
    setAdyenError(null);
    setSubmitting(false);
    onClose();
  };

  const handleDone = () => {
    if (business) onSubmitted?.(business._id, onboardingUrl);
    handleClose();
  };

  const isMissingWebsite = adyenError?.code === "MISSING_WEBSITE";

  const handleAddWebsite = () => {
    const target = business;
    handleClose();
    if (target) onAddWebsite?.(target);
  };

  const handleYes = async () => {
    if (!business) return;
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setSubmitting(true);
    try {
      const result = await adyenOnboardingServer({
        businessId: business._id,
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      setOnboardingUrl(result.merchantHubOnboardingLinkUrl);
      setStep("success");
    } catch (err: any) {
      if (controller.signal.aborted || axios.isCancel(err)) return;
      setAdyenError(err);
      setStep("error");
    } finally {
      if (!controller.signal.aborted) setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!visible || !business) return;
    setOnboardingUrl("");
    setAdyenError(null);
    setStep("confirm");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, business?._id]);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const handleCopyErrorDetails = () => {
    if (!adyenError) return;
    const text = [
      `Code: ${adyenError.code ?? "-"}`,
      `Message: ${adyenError.message ?? "-"}`,
      `Status: ${adyenError.statusCode ?? "-"}`,
    ].join("\n");
    navigator.clipboard.writeText(text).then(() => message.success("Copied!"));
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(onboardingUrl).then(() => {
      message.success("Link copied!");
    });
  };

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      centered
      width={480}
      title={null}
      closable={step === "confirm" || step === "error" || step === "loading"}
    >
      {step === "loading" && (
        <div className={styles.confirm_body}>
          <div className={styles.confirm_heading}>
            <Spin size="large" />
            <p className={styles.confirm_title} style={{ marginTop: 16 }}>
              Generating onboarding link
            </p>
            <p className={styles.confirm_subtitle}>
              Please wait while we create a fresh onboarding link for{" "}
              <strong>{business?.businessName}</strong>.
            </p>
          </div>
        </div>
      )}

      {step === "confirm" && (
        <div className={styles.confirm_body}>
          <div className={styles.confirm_heading}>
            <p className={styles.confirm_title}>
              Submit {business?.businessName} to Adyen?
            </p>
            <p className={styles.confirm_subtitle}>
              This action will send the merchant for processing.
            </p>
          </div>
          <div className={styles.confirm_buttons}>
            <button
              className={styles.confirm_btn_secondary}
              onClick={handleClose}
            >
              No
            </button>
            <button
              className={styles.confirm_btn_primary}
              onClick={handleYes}
              disabled={submitting}
            >
              {submitting ? "Submitting..." : "Yes, Submit"}
            </button>
          </div>
        </div>
      )}

      {step === "error" && isMissingWebsite && (
        <div className={styles.error_body}>
          <div className={styles.error_icon}>
            <svg
              width="44"
              height="44"
              viewBox="0 0 44 44"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="22"
                cy="22"
                r="20"
                stroke="#f59e0b"
                strokeWidth="2.5"
              />
              <path
                d="M22 13V25"
                stroke="#f59e0b"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
              <circle cx="22" cy="31" r="1.6" fill="#f59e0b" />
            </svg>
          </div>
          <p className={styles.error_title}>Add a Website First</p>
          <p className={styles.error_subtitle}>
            <strong>{business?.businessName}</strong> has no website on record.
            Add the merchant's website in the <strong>Company Profile</strong>{" "}
            tab (Website field), then submit to Adyen again.
          </p>
          <div className={styles.error_buttons}>
            <button
              className={styles.confirm_btn_secondary}
              onClick={handleClose}
            >
              Close
            </button>
            <button
              className={styles.confirm_btn_primary}
              onClick={handleAddWebsite}
            >
              Add website
            </button>
          </div>
        </div>
      )}

      {step === "error" && !isMissingWebsite && (
        <div className={styles.error_body}>
          <div className={styles.error_icon}>
            <svg
              width="44"
              height="44"
              viewBox="0 0 44 44"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="22"
                cy="22"
                r="20"
                stroke="#ef4444"
                strokeWidth="2.5"
              />
              <path
                d="M15 15L29 29M29 15L15 29"
                stroke="#ef4444"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <p className={styles.error_title}>Submission to Adyen Failed</p>
          <p className={styles.error_subtitle}>
            <strong>{business?.businessName}</strong> could not be submitted to
            Adyen. Review the errors below, fix the highlighted fields on the
            merchant's profile, and try again.
          </p>
          <div className={styles.error_card}>
            <div className={styles.error_card_header}>
              <span className={styles.error_card_label}>Adyen Response</span>
              <span className={styles.error_http_badge}>
                ●{" "}
                {adyenError?.statusCode
                  ? `HTTP ${adyenError.statusCode}`
                  : (adyenError?.code ?? "Error")}
              </span>
            </div>
            <div className={styles.error_row}>
              <span className={styles.error_field_name}>Error code</span>
              <span className={styles.error_field_value}>
                {adyenError?.code ?? "-"}
              </span>
            </div>
            <div className={styles.error_row}>
              <span className={styles.error_field_name}>Message</span>
              <span className={styles.error_field_value}>
                {adyenError?.message ?? "-"}
              </span>
            </div>
            {adyenError?.invalidFields &&
              adyenError.invalidFields.length > 0 && (
                <div className={styles.invalid_fields_section}>
                  <span className={styles.invalid_fields_label}>
                    INVALID FIELDS ({adyenError.invalidFields.length})
                  </span>
                  {adyenError.invalidFields.map((field, index) => (
                    <div
                      key={`${field.name ?? "field"}-${index}`}
                      className={styles.invalid_field_row}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        style={{ flexShrink: 0, marginTop: 2 }}
                      >
                        <circle
                          cx="8"
                          cy="8"
                          r="7"
                          stroke="#ef4444"
                          strokeWidth="1.5"
                        />
                        <path
                          d="M5.5 5.5L10.5 10.5M10.5 5.5L5.5 10.5"
                          stroke="#ef4444"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className={styles.invalid_field_message}>
                        {field.name && (
                          <div style={{ fontWeight: 600 }}>{field.name}</div>
                        )}
                        {field.message}
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
          <div className={styles.error_buttons}>
            <button
              className={styles.confirm_btn_secondary}
              onClick={handleCopyErrorDetails}
            >
              Copy error details
            </button>
            <button
              className={styles.confirm_btn_primary}
              onClick={handleClose}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {step === "success" && (
        <div className={styles.success_body}>
          <div className={styles.success_icon}>
            <svg
              width="44"
              height="44"
              viewBox="0 0 44 44"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="22"
                cy="22"
                r="20"
                stroke="#22C55E"
                strokeWidth="2.5"
              />
              <path
                d="M13 22L19 28L31 16"
                stroke="#22C55E"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <p className={styles.success_title}>
            Successfully Submitted to Adyen
          </p>

          <p className={styles.success_subtitle}>
            {business?.businessName} has been submitted to Adyen. Share the
            onboarding link below with the merchant.
          </p>

          <div className={styles.link_card}>
            <span className={styles.link_label}>
              Adyen Hosted Onboarding Link
            </span>
            <div className={styles.link_row}>
              <Input
                className={styles.link_input}
                value={onboardingUrl}
                readOnly
              />
              <Button className={styles.copy_btn} onClick={handleCopy}>
                Copy
              </Button>
            </div>
          </div>

          <div className={styles.note_box}>
            <p className={styles.note_text}>
              Note: You can also access this link later from the Acquirer Status
              column in the merchant table.
            </p>
          </div>

          <button className={styles.done_btn} onClick={handleDone}>
            Done
          </button>
        </div>
      )}
    </Modal>
  );
}
